import type { FC } from 'react'
import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { GetClientPublicationsQueryParamsType } from '@/api/kubik/infoportal'
import { RubricsMenu } from '@/components/composed'
import { MetaInfo, PaginationMoreButton, Skeleton } from '@/components/ui'
import { pushGtmEvent } from '@/logic/metrika/helpers'
import type { IFormattedGtmEventPayload } from '@/logic/metrika/interfaces'
import { FieldEvaluationReportDialogLauncher } from '@/pages/Financing/components/FieldEvaluationReportDialogLauncher'
import { formatLocalizedString } from '@/utils/formatUtils'
import { useInfiniteJournalPublications } from '@/utils/infoportal/hooks/useInfiniteJournalPublications'
import { useJournalRubrics } from '@/utils/infoportal/hooks/useJournalRubrics'
import { useJournalUrlParams } from '@/utils/infoportal/hooks/useJournalUrlParams'
import { JournalNewsItem } from './components'
import { newsSkeletons } from './constants'
import { messages } from './messages'
import './styles.scss'

const JournalNews: FC = () => {
  const params = useJournalUrlParams()
  const { pathname } = useLocation()

  const { rubricId } = params
  const { currentRubric } = useJournalRubrics(Number(rubricId))

  const {
    isPublicationsInitialLoading,
    publications: news,
    isPublicationsLoading,
    hasNextPage,
    getNextPage,
  } = useInfiniteJournalPublications({
    type: GetClientPublicationsQueryParamsType.NEWS,
    rubricId: rubricId ? Number(rubricId) : undefined,
  })

  useEffect(() => {
    pushGtmEvent<IFormattedGtmEventPayload>({
      event: 'userEvent',
      pagePart: 'content',
      blockName: null,
      eventCategory: 'interactions',
      eventAction: 'viewing',
      eventLabel: 'pageView',
      eventContext: `pp:${pathname};pt:other`,
      eventStatus: 'success',
      eventEcommerce: null,
      eventNonInteraction: '0',
      firingOptions: 'oncePerEvent',
      ecommerce: null,
    })
  }, [pathname])

  return (
    <>
      {currentRubric && (
        <MetaInfo
          metaInfo={{
            title: formatLocalizedString(messages.reactHelmetTitle, { rubricName: currentRubric.name }),
          }}
        />
      )}

      <RubricsMenu />

      <section className="page-wrapper page-wrapper_no-mobile journal-page journal-news-announce-container">
        {currentRubric && <h1 className="space-holder32">{currentRubric.name}</h1>}

        {(isPublicationsInitialLoading || !!news?.length) && (
          <h2 className="space-holder56 page-wrapper_mobile">{messages.articlesHeader}</h2>
        )}

        <Skeleton isLoading={isPublicationsInitialLoading} skeletons={newsSkeletons}>
          {news?.map((newsItem, index) => (
            <JournalNewsItem
              newsItem={newsItem}
              isFirst={index === 0}
              previousNewsItemPublicationDate={news[index - 1]?.publicationDate}
              key={newsItem.id}
            />
          ))}
        </Skeleton>

        <PaginationMoreButton
          className="space-holder-top48"
          isLoading={isPublicationsLoading}
          hasNextPage={hasNextPage}
          onClick={getNextPage}
        />
      </section>
      <FieldEvaluationReportDialogLauncher comment={messages.fieldEvaluationReportDialog.comment} />
    </>
  )
}

export default JournalNews
